import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	useLocation,
	useHistory
} from 'react-router-dom';

import { downloadAsset } from '../../../../../api/nora/assets';
import {
	getProgramSubmissions,
	exportProgramSubmissions
} from '../../../../../api/nora/programs';
import { getSubmissionReport } from '../../../../../api/nora/reports';
import { getDocument } from '../../../../../api/nora/submissions';

// import { useAuthState } from '../../../../../contexts/AuthContext';
import {
	useProgramState,
	useProgramAction
} from '../../../../../contexts/ProgramsContext';
import {
	useSubmissionAction,
	useSubmissionState
} from '../../../../../contexts/SubmissionsContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import DateCell from '../../../../Table/Cells/DateCell';
import DocumentModal from '../../../../DocumentModal';
import HelpModal from '../../../../Shared/HelpModal';
import MultiSelectColumnFilter from '../../../../Table/ColumnFilters/MultiSelectColumnFilter';
import multiSelectFilterFn from '../../../../Table/filterFunctions/multiSelectFilterFn';
import SectionHeader from '../../../../Shared/SectionHeader';
import Table from '../../../../Table';
import TableRowActions from '../../../../Table/Cells/TableRowActions';
import multiWordFilterFn from '../../../../Table/filterFunctions/multiWordFilterFn';
import dateFilterFn from '../../../../Table/filterFunctions/dateFilterFn';
import NewSubmissionModal from './NewSubmissionModal';

const SubmissionsTab = () => {
	const { state: navigationState } = useLocation();
	const history = useHistory();

	// const { user: { permissions } } = useAuthState();
	const {
		override,
		program,
		submissions,
		submissionsTableState
	} = useProgramState();
	const {
		resetSubmissionsTableState,
		setSubmissions,
		setSubmissionsTableState
	} = useProgramAction();
	const { document } = useSubmissionState();
	const { setDocument } = useSubmissionAction();
	const {
		setApiErrorAlert,
		setInfobarLoader,
		setScreenLoader
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ documentModal, setDocumentModal ] = useState( false );
	const [ newSubmissionModal, setNewSubmissionModal ] = useState( false );

	const retrieveSubmissions = useCallback( async () => {
		setInfobarLoader( 'Retrieving Submissions...' );

		try {
			const data = await getProgramSubmissions( program?.id );

			setSubmissions( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		program,
		setSubmissions,
		setApiErrorAlert
	] );

	const retrieveDocument = useCallback( async id => {
		try {
			setScreenLoader( 'Retrieving Submission...' );
			const document = await getDocument( id );

			setDocument( document );
			setDocumentModal( true );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setDocument,
		setScreenLoader
	] );

	useEffect( () => {
		( async () => {
			await retrieveSubmissions();

			if ( navigationState?.submissionID ) {
				await retrieveDocument( navigationState.submissionID );
				history.replace( location.pathname, undefined );
			}

			setIsLoading( false );
		} )();

		return () => {
			resetSubmissionsTableState();
		};
	}, [
		history,
		navigationState?.submissionID,
		retrieveSubmissions,
		resetSubmissionsTableState,
		setDocument,
		retrieveDocument
	] );

	const dateCreatedCell = useCallback( ( { row } ) => {
		if ( row.original.createdOn === '0001-01-01T00:00:00' ) {
			return <p>N/A</p>;
		}

		return <DateCell date={ row.original.createdOn } />;
	}, [] );

	const submissionDateCell = useCallback( ( { row } ) => {
		if ( row.original.submissionDate === '0001-01-01T00:00:00' ) {
			return <p>N/A</p>;
		}

		return <DateCell date={ row.original.submissionDate } />;
	}, [] );

	const onView = useCallback( ( { id } ) => {
		retrieveDocument( id );
	}, [ retrieveDocument ] );

	const onDownload = useCallback( async ( { id } ) => {
		setScreenLoader( 'Retreiving Submission Report...' );

		try {
			await getSubmissionReport( id );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader
	] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				icon: 'search',
				permission: true,
				text: 'View Submission'
			},
			{
				action: onDownload,
				componentProps: { color: 'pink' },
				icon: 'file pdf',
				permission: true,
				text: 'Download Submission'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onDownload,
		onView
	] );

	const columns = useMemo( () => [
		{
			accessor: 'name',
			Header: 'Name',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			accessor: 'agencyName',
			Header: 'Agency',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'type',
			accessor: row => `${ row.type }`,
			Header: 'Document Type',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		},
		// THIS NEEDS TO BE HERE!!!!!!!
		{
			accessor: 'createdOn',
			Header: 'Created On',
			filter: multiWordFilterFn,
			Cell: dateCreatedCell,
			minWidth: 60
		},
		{
			accessor: 'submissionDate',
			Header: 'Submission Date',
			filter: dateFilterFn,
			Cell: submissionDateCell,
			minWidth: 60
		},
		{
			id: 'submissionStatus',
			accessor: row => `${ row.submissionStatus.name }`,
			Header: 'Submission Status',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		},
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}
	], [
		dateCreatedCell,
		submissionDateCell,
		tableRowActionsCell
	] );

	const addSubmission = useCallback( () => {
		setNewSubmissionModal( true );
	}, [] );

	const exportAll = useCallback( async () => {
		setScreenLoader( 'Exporting submissions, Please wait...' );

		try {
			const ids = submissions.map( ( { id } ) => id );
			const guid = await exportProgramSubmissions( ids );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader,
		submissions
	] );

	const exportFiltered = useCallback( async () => {
		setScreenLoader( 'Exporting submissions, Please wait...' );

		try {
			const ids = submissionsTableState.rows.map( row => row.original.id );
			const guid = await exportProgramSubmissions( ids );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader,
		submissionsTableState.rows
	] );

	const exportSelected = useCallback( async () => {
		setScreenLoader( 'Exporting submissions, Please wait...' );

		try {
			const ids = submissionsTableState.selectedIds;
			const guid = await exportProgramSubmissions( ids );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader,
		submissionsTableState.selectedIds
	] );

	const actions = useMemo( () => [
		{
			action: {
				'All Rows': exportAll,
				...submissionsTableState.filters.length
					? { 'Filtered Rows': exportFiltered }
					: {},
				...submissionsTableState.selectedIds.length
					? { 'Selected Rows': exportSelected }
					: {}
			},
			disable: false,
			dropdown: true,
			helpId: 4,
			icon: 'cog',
			permission: true,
			text: 'Export CSV'
		},
		{
			action: addSubmission,
			disabled: false,
			helpId: 23,
			icon: 'add',
			permission: true,
			text: 'New Submission'
		}
	], [
		addSubmission,
		exportAll,
		exportFiltered,
		exportSelected,
		submissionsTableState.filters.length,
		submissionsTableState.selectedIds.length
	] );

	const closeNewSubmissionModal = useCallback( () => {
		setNewSubmissionModal( false );
		retrieveSubmissions();
	}, [ retrieveSubmissions ] );

	const closeDocumentModal = useCallback( () => {
		setDocumentModal( false );
		setDocument( null );
		retrieveSubmissions();
	}, [
		retrieveSubmissions,
		setDocument
	] );

	if ( isLoading ) {
		return null;
	}

	return (
		<>
			<SectionHeader
				actions={ actions }
				title={
					<>
						<span>Program Submissions</span>
						<HelpModal
							id={ override?.id === 'onboarding' ? 40 : 12 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			{ submissions.length
				? <Table
					columns={ columns }
					data={ submissions }
					selectable
					setTableState={ setSubmissionsTableState }
					tableState={ submissionsTableState }
					showCount={ submissions.length > 10 }
					showEmptyRows={ false }
					showPagination={ submissions.length > 10 }
				/>
				: 'no submissions available for this program.'
			}
			<DocumentModal
				canSubmit={ document && document?.type.id === 98 }
				closeModal={ closeDocumentModal }
				open={ document && documentModal }
			/>
			<NewSubmissionModal
				open={ newSubmissionModal }
				onClose={ closeNewSubmissionModal }
			/>
		</>
	);
};

SubmissionsTab.propTypes = {};

export default SubmissionsTab;
